import { LitElement, html, nothing, TemplateResult } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { style } from './speed-order-slide-in.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { type CSESlideIn } from '@cloudsuite/mosaic-elements'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { type CorrectionItem, type ErrorItem } from 'components/ordering/speed-order/services/speed-order-upload-api.js'


@customElement('csc-speed-order-slide-in')
export class CSCSpeedOrderSlideIn extends LitElement {
  static override styles = [style, hostStyle]

  @property({ type: String, attribute: 'variant' })
  variant: 'success' | 'error' | 'warning' = 'success'

  @property({ type:Boolean, attribute:'has-headers' })
  hasHeaders = false

  @property({ type: Array, attribute: 'column-headers' })
  columnHeaders: string[] = []

  @property({ type: Array, attribute: 'errors' })
  errors: ErrorItem[] = []

  @property({ type: Number, attribute: 'records' })
  records = 0

  @property({ type: Array })
  corrections: CorrectionItem[] = []
  
  @state()
  private selectedCodeColumn = 0
  
  @state()
  private selectedQuantityColumn = 1
  
  @state()
  private internalHasHeaders = false
  
  @query('cse-slide-in')
  private slideIn!: CSESlideIn

  public open(): void {
    this.slideIn.open()
    // Initialize internal state from properties
    this.internalHasHeaders = this.hasHeaders
  }

  public close(): void {
    this.slideIn.close()
  }

  private handleHeadersChange = (event: CustomEvent) => {
    const radioButton = event.target as any
    this.internalHasHeaders = radioButton.value === '1'
  }

  private handleCodeColumnChange = (event: CustomEvent) => {
    const select = event.target as any
    this.selectedCodeColumn = parseInt(select.value)
  }

  private handleQuantityColumnChange = (event: CustomEvent) => {
    const select = event.target as any
    this.selectedQuantityColumn = parseInt(select.value)
  }

  private handleUploadCSV = () => {
    // Dispatch event with CSV configuration data
    this.dispatchEvent(new CustomEvent('upload-csv', {
      bubbles: true,
      composed: true,
      detail: {
        hasHeaders: this.internalHasHeaders,
        codeColumn: this.selectedCodeColumn,
        quantityColumn: this.selectedQuantityColumn
      }
    }))
  }

  private handleImportRemainingProducts = () => {
    // Dispatch event to close slide-in and proceed with import
    this.dispatchEvent(new CustomEvent('import-remaining-products', {
      bubbles: true,
      composed: true
    }))
  }

  private renderSuccess(): TemplateResult {
    const displayHeaders = this.internalHasHeaders
    const columnOptions = this.columnHeaders.length > 0 ? this.columnHeaders : []

    return html`
      <div slot="header">
        ${t('csv_upload_header', { ns: 'csc-speed-order-slide-in', default: 'CSV upload' })}
      </div>
      
      <cse-alert variant='success' icon-name='check' icon-style='light'>
        <cse-text slot='description'>
          ${t('csv_upload_success_description', { ns: 'csc-speed-order-slide-in', default: 'Uploaded CSV file is successfully read.' })}
        </cse-text>
      </cse-alert>

      <cse-fieldset>
        <cse-form-field>
          <cse-label slot="label">
            ${t('column_headers_label', { ns: 'csc-speed-order-slide-in', default: 'Column headers' })}
          </cse-label>
          <cse-radio-button-group name="group1" inline @change="${this.handleHeadersChange}">
            <cse-radio-button id="rb1" value="1" ?checked="${displayHeaders}">
              ${t('column_headers_yes', { ns: 'csc-speed-order-slide-in', default: 'Yes' })}
            </cse-radio-button>
            <cse-radio-button id="rb2" value="2" ?checked="${!displayHeaders}">
              ${t('column_headers_no', { ns: 'csc-speed-order-slide-in', default: 'No' })}
            </cse-radio-button>
          </cse-radio-button-group>
        </cse-form-field>
      
        <cse-form-field>
          <cse-label slot="label">
            ${t('column_product_code_label', { ns: 'csc-speed-order-slide-in', default: 'Column product code' })}
          </cse-label>
          <cse-select 
            placeholder="${t('column_code_placeholder', { ns: 'csc-speed-order-slide-in', default: 'Product code' })}" 
            @change="${this.handleCodeColumnChange}"
          >
            ${columnOptions.map((header, index) => html`
              <cse-option value="${index}" ?selected="${index === this.selectedCodeColumn}">
                ${displayHeaders ? header : `Column ${index + 1}`}
              </cse-option>
            `)}
          </cse-select>
        </cse-form-field>
        
        <cse-form-field>
          <cse-label slot="label">
            ${t('column_quantity_label', { ns: 'csc-speed-order-slide-in', default: 'Column Quantity' })}
          </cse-label>
          <cse-select 
            placeholder="${t('column_quantity_placeholder', { ns: 'csc-speed-order-slide-in', default: 'Quantity' })}" 
            @change="${this.handleQuantityColumnChange}"
          >
            ${columnOptions.map((header, index) => html`
              <cse-option value="${index}" ?selected="${index === this.selectedQuantityColumn}">
                ${displayHeaders ? header : `Column ${index + 1}`}
              </cse-option>
            `)}
          </cse-select>
        </cse-form-field>
      </cse-fieldset>

      <cse-button slot="footer" fit='fill' variant="default" @click="${() => this.close()}">
        ${t('cancel_button', { ns: 'csc-speed-order-slide-in', default: 'Cancel' })}
      </cse-button>
      <cse-button slot="footer" fit='fill' variant="primary" @click="${this.handleUploadCSV}">
        ${t('upload_csv_button', { ns: 'csc-speed-order-slide-in', default: 'Upload CSV' })}
      </cse-button>
    `
  }
  private renderReviewProduct(productCode: string, productName: string, quantity: string, errorMessage: string): TemplateResult {
    return html`
      <cse-list-item>
        <div class="test">
          <div class="product-details" fit='fill'>
            <cse-text variant="product-title-m">${productCode}</cse-text>
            <cse-text>${productName}</cse-text>
            <cse-text>${quantity}x</cse-text>
          </div>
          <cse-badge variant="warning" composition="simple">
            ${errorMessage}
            <cse-icon slot="prefix" icon-name="circle-info" icon-style="solid" aria-hidden="true"></cse-icon>
          </cse-badge>
        </div>
      </cse-list-item>
    `
  }
  private renderError(): TemplateResult {
    return html`
      <div slot="header">
        ${t('csv_upload_header', { ns: 'csc-speed-order-slide-in', default: 'CSV upload' })}
      </div>
      
      <cse-alert variant="error">
        <cse-text slot='description'>
          ${t('csv_upload_error_description', { ns: 'csc-speed-order-slide-in', default: 'The uploaded CSV file contains errors, please check the following products.' })}
        </cse-text>
      </cse-alert>

      <cse-list divider>
        ${this.errors.map(error =>
          this.renderReviewProduct(error.product_number, error.name, String(error.quantity), error.message)
        )}
      </cse-list>
      
      <cse-button variant="default" slot="footer" fit='fill' @click="${() => this.close()}">
        ${t('cancel_button', { ns: 'csc-speed-order-slide-in', default: 'Cancel' })}
      </cse-button>
      ${this.records > 0 ? html`
        <cse-button  variant="primary" @click="${this.handleImportRemainingProducts}" slot="footer" fit='fill'>
          ${t('import_remaining_products_button', { ns: 'csc-speed-order-slide-in', default: 'Import remaining products ({{variable_count}})', variable_count: this.records })}
        </cse-button>
      ` : html`
        <cse-button disabled variant="primary" slot="footer" fit='fill'>
          ${t('import_products_button', { ns: 'csc-speed-order-slide-in', default: 'Import products' })}
        </cse-button>
      `}
      
    `
  }
 
  private renderCorrectionItem(correction: CorrectionItem): TemplateResult {
    return html`
      <cse-list-item>
        <div class="test">
          <div class="product-details" fit='fill'>
            <cse-text variant="product-title-m">${correction.product_number}</cse-text>
            <cse-text>${correction.name}</cse-text>
            <div class="quantity-correction">
              <cse-text class="quantity-original">${correction.original_quantity}x</cse-text>
              <cse-text>${correction.suggested_quantity}x</cse-text>
            </div>
          </div>
          <cse-badge variant="info" composition="simple">
            ${t('quantity_adjusted_message', { ns: 'csc-speed-order-slide-in', default: 'Changed to match (minimum) order quantity' })}
            <cse-icon slot="prefix" icon-name="circle-info" icon-style="solid" aria-hidden="true"></cse-icon>
          </cse-badge>
        </div>
      </cse-list-item>
    `
  }

  private renderWarning(): TemplateResult {
    type ListItem = { type: 'error', row: number, data: ErrorItem } | { type: 'correction', row: number, data: CorrectionItem }
    const combined: ListItem[] = [
      ...this.errors.map(e => ({ type: 'error' as const, row: e.row, data: e })),
      ...this.corrections.map(c => ({ type: 'correction' as const, row: c.row, data: c })),
    ].sort((a, b) => a.row - b.row)

    return html`
      <div slot="header">
        ${t('csv_upload_header', { ns: 'csc-speed-order-slide-in', default: 'CSV upload' })}
      </div>

      ${this.errors.length > 0 ? html`
        <cse-alert variant="warning">
          <cse-text slot='description'>
            ${t('csv_errors_description', { ns: 'csc-speed-order-slide-in', default: '{{variable_count}} products could not be added', variable_count: this.errors.length })}
          </cse-text>
        </cse-alert>
      ` : nothing}

      <cse-alert variant="info">
        <cse-text slot='description'>
          ${t('csv_corrections_description', { ns: 'csc-speed-order-slide-in', default: 'The quantity of {{variable_count}} products has been changed', variable_count: this.corrections.length })}
        </cse-text>
      </cse-alert>

      <cse-list divider>
        ${combined.map(item =>
          item.type === 'error'
            ? this.renderReviewProduct(item.data.product_number, item.data.name, String(item.data.quantity), item.data.message)
            : this.renderCorrectionItem(item.data)
        )}
      </cse-list>

      <cse-button variant="default" slot="footer" fit='fill' @click="${() => this.close()}">
        ${t('cancel_button', { ns: 'csc-speed-order-slide-in', default: 'Cancel' })}
      </cse-button>
      ${this.records > 0 ? html`
        <cse-button variant="primary" @click="${this.handleImportRemainingProducts}" slot="footer" fit='fill'>
          ${t('import_remaining_products_button', { ns: 'csc-speed-order-slide-in', default: 'Import remaining products ({{variable_count}})', variable_count: this.records })}
        </cse-button>
      ` : html`
        <cse-button disabled variant="primary" slot="footer" fit='fill'>
          ${t('import_products_button', { ns: 'csc-speed-order-slide-in', default: 'Import products' })}
        </cse-button>
      `}
    `
  }

  override render(): TemplateResult {
    return html`
        <cse-slide-in id="default" size='default'>
            ${this.variant === 'success' ? this.renderSuccess() : this.variant === 'warning' ? this.renderWarning() : this.renderError()}
        </cse-slide-in>
    `
  }
}

declare global {
    interface HTMLElementTagNameMap {
        'csc-speed-order-slide-in': CSCSpeedOrderSlideIn
    }
}
